import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Trans, useTranslation } from 'react-i18next';
import { AiOutlineLoading } from 'react-icons/ai';
import Button from '../components/Button/Button';
import { getActiveCourse } from '../services/settings';
import useFormValidation from '../hooks/useFormValidation';
import { DatePicker } from 'antd';
import dayjs from "dayjs";
import { checkEligibilityFormValidationSchema } from '../utils/formValidation';
import { errorToast } from '../utils/errorHandling';
import CustomModal from '../components/CustomModal';
import { checkEligibility, resendAdmissionEmail } from '../services/admissionApi';

const SearchEligibility = () => {
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(false);
    const [isSendingEmail, setIsSendingEmail] = useState(false);
    const [formData, setFormData] = useState({ mobile: "", birthdate: "" });
    const [courseData, setCourseData] = useState(null);
    const { errors, validateField, validateAllFields } = useFormValidation(checkEligibilityFormValidationSchema(t));
    const [eligibility, setEligibility] = useState(null);
    const [eligibilityModalVisible, setEligibilityModalVisible] = useState(false);
    const lang = localStorage.getItem('i18nextLng');

    const handleInputChange = (e) => {
        const { id, value } = e.target;
        if (value !== '') {
            if (/^[1-9]\d*$/.test(value)) {
                setFormData((prevState) => ({
                    ...prevState,
                    [id]: value,
                }));
            }
        } else {
            setFormData((prevState) => ({
                ...prevState,
                [id]: value,
            }));
        }
    };

    const fetchActiveCourse = async () => {
        setIsLoading(true);
        try {
            const response = await getActiveCourse();
            const data = response.data.data;

            if (response.status === 200) {
                if (data.length === 0) {
                    setCourseData(null);
                } else {
                    setCourseData(data[0]);
                }
            } else {
                toast.error(t('general.ActionFailed'));
            }
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDateChange = (e) => {
        setFormData((prevState) => ({
            ...prevState,
            "birthdate": e.format("YYYY-MM-DD"),
        }));
    };

    const handleResendAdmissionEmail = async () => {
        try {
            setIsSendingEmail(true);
            const response = await resendAdmissionEmail({
                user_id: eligibility.user.user_id,
                email: eligibility.user.email,
                lang: lang === "en"? "en": "cn",
                course_id: courseData.id
            });
            const data = response.data;
            console.log('date', data)
            toast.success(t("eligibilityCheck.resentEmailSuccess"));
        } catch (error) {
            console.log("Account blocked", error?.response?.data?.error === "Account blocked", error?.response?.data?.error)
            if (error?.response?.data?.error === "Account blocked") {
                errorToast(null, t("eligibilityCheck.accountBlocked"), t);
            }
            else {
                errorToast(error, t("eligibilityCheck.resentEmailFailed"), t);
            }
        } finally {
            setIsSendingEmail(false);
        }
    }

    useEffect(() => {
        fetchActiveCourse();
    }, []);

    const handleSubmit = async () => {
        const includeFields = Object.keys(formData);
        if (validateAllFields(formData, includeFields)) {
            try {
                setIsLoading(true);
                const response = await checkEligibility(formData);
                const data = response.data;
                if (data) {
                    setEligibility(data);
                    setEligibilityModalVisible(true);
                }
            } catch (error) {
                errorToast(error, t("general.ActionFailed"), t);
            } finally {
                setIsLoading(false);
            }
        } else {
            toast.error(t("general.ValidationFail"));
        }
    };

    return (
        <>
            {
                isLoading && !courseData ?
                    <div className='m-5 d-flex justify-content-center'>
                        <AiOutlineLoading className='spin' />
                    </div>
                    :
                    !courseData ?
                        <>
                            <div className='form-wrapper d-flex flex-column'>
                                <h3 className='mt-5 d-flex justify-content-center'>{t("login.notOpen")}</h3>
                            </div>
                        </>

                        :
                        <>
                            <div className='form-wrapper d-flex flex-column'>
                                <h3 className='mt-5 d-flex justify-content-center'>{t("navbar.eligibilityCheck")}</h3>
                                <p className='d-flex justify-content-center text-center'>{lang === "en" ? courseData.course_ename : courseData.course_cname}</p>

                                <form>
                                    <div className="input-group d-flex justify-content-center">
                                        <input
                                            type="text"
                                            value={formData?.mobile || ""}
                                            className="form-control border-0 "
                                            id="mobile"
                                            placeholder={t("personal_info.mobile")}
                                            onChange={handleInputChange}
                                            name="mobile"
                                            required
                                        />
                                    </div>
                                    <div className='d-flex justify-content-center border-none'>
                                        {errors.mobile && <span className="error">{errors.mobile}</span>}
                                    </div>
                                    <div className="password-input mt-3">
                                        <DatePicker
                                            className='form-control border-0 w-100'
                                            placeholder={t("personal_info.birthdate")}
                                            value={formData.birthdate ? dayjs(formData.birthdate, "YYYY-MM-DD") : null}
                                            onChange={handleDateChange}
                                            inputReadOnly={true}
                                            format="YYYY-MM-DD"
                                        />
                                    </div>
                                    <div className='d-flex justify-content-center'>
                                        {errors.birthdate && <span className="error">{errors.birthdate}</span>}
                                    </div>
                                    <div className='submit-button mt-4 d-flex justify-content-center'>
                                        <Button buttonStyle='btn--primary' disabled={isLoading} onClick={handleSubmit} type='button'>
                                            {isLoading ? <AiOutlineLoading className="spin" /> : t('eligibilityCheck.check')}
                                        </Button>
                                    </div>
                                </form>
                            </div>
                            <CustomModal
                                open={eligibilityModalVisible}
                                hideModal={() => { setEligibilityModalVisible(false) }}
                                performAction={() => { setEligibilityModalVisible(false) }}
                            >
                                <div
                                    className="m-3 d-flex flex-column"
                                >
                                    {eligibility?.eligible && (
                                        <>
                                            <h6
                                                style={{
                                                    marginBottom: 16,
                                                    whiteSpace: "pre-line",
                                                    textAlign: "center",
                                                    fontWeight: 400,
                                                    lineHeight: 1.0
                                                }}
                                            >
                                                <Trans
                                                    i18nKey="eligibilityCheck.acceptedMessage"
                                                    values={{
                                                        name:
                                                            lang === "en"
                                                                ? eligibility.user.ename
                                                                : eligibility.user.cname,
                                                        email: eligibility.user.email,
                                                        course_name: lang === "en" ? courseData.course_ename : courseData.course_cname,
                                                        participation_type: eligibility.user.participation_type === "Online" ? t("eligibilityCheck.online") : t("eligibilityCheck.inPerson")
                                                    }}
                                                    components={{ bold: <b /> }}
                                                />
                                            </h6>

                                            <div
                                                className="action-buttons d-flex justify-content-center mt-3"
                                                style={{ marginTop: "auto" }}
                                            >
                                                <Button
                                                    buttonStyle="btn--primary"
                                                    buttonSize="btn--small"
                                                    onClick={handleResendAdmissionEmail}
                                                >
                                                    {isSendingEmail ? <AiOutlineLoading className="spin" /> : t('general.sendEmail')}
                                                </Button>
                                            </div>
                                        </>
                                    )}
                                    {!eligibility?.eligible && (
                                        <h6
                                                style={{
                                                    marginBottom: 16,
                                                    whiteSpace: "pre-line",
                                                    textAlign: "center",
                                                    fontWeight: 400,
                                                    lineHeight: 1.0
                                                }}
                                            >
                                                <Trans
                                                    i18nKey="eligibilityCheck.rejectedMessage"
                                                    components={{ bold: <b /> }}
                                                />
                                            </h6>
                                    )}

                                </div>
                            </CustomModal>
                        </>
            }
        </>
    )
}
export default SearchEligibility